import { CURRENCY, QuoteItem } from "../../../../reducers/uploadedBoard";
import React, { useContext, useState } from "react";
import LoadingIcon from "../../../common/LoadingIcon";
import { LineItemDescription } from "../../../common/LineItem/LineItemStyles";
import {
  ItemsModalContent,
  QuoteHistoryContainer,
  QuoteHistoryItem,
  ShipmentRow,
  ShipmentsColumn,
  ShipmentsTitle,
  Title,
} from "./QuoteHistoryStyles";
import {
  datetimeStringToHumanReadableDatetime,
  formatCurrency,
} from "../../../../utils/valueFormatting";
import { FakeLink, InputLabel } from "../../../../constants/Styles";
import Modal from "../../../common/Modal";
import ModalContentWithCloseButton from "../../../common/ModalContentWithCloseButton";
import { QuoteBreakdownItems } from "../Breakdown";
import useQuoteHistory, {
  QuoteHistoryShipment,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/quoteHistory";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";
import { QuoteToolContainer } from "../QuoteStyles";

function formatLeadTime(leadTimeDays: number | null) {
  if (leadTimeDays === null) {
    return "Unknown";
  }
  return `${leadTimeDays.toLocaleString()} days`;
}

function QuoteShipments({ shipments }: { shipments: QuoteHistoryShipment[] }) {
  return (
    <ShipmentsColumn>
      <ShipmentsTitle>Shipments</ShipmentsTitle>
      {shipments.length === 0 && (
        <LineItemDescription>
          No shipment details were recorded with this quote
        </LineItemDescription>
      )}
      {shipments.map((shipment) => {
        return (
          <ShipmentRow key={shipment.shipment_id}>
            <div>
              <InputLabel>Quantity:</InputLabel>
              <div>{shipment.num_boards.toLocaleString()}</div>
            </div>
            <div>
              <InputLabel>Lead time:</InputLabel>
              <div>{formatLeadTime(shipment.lead_time_days)}</div>
            </div>
          </ShipmentRow>
        );
      })}
    </ShipmentsColumn>
  );
}

function QuoteItems({
  items,
  shipments,
  currency,
}: {
  items: QuoteItem[];
  shipments: QuoteHistoryShipment[];
  currency: CURRENCY;
}) {
  const [seeItems, setSeeItems] = useState(false);

  const closeModal = () => {
    setSeeItems(false);
  };

  return (
    <>
      <FakeLink onClick={() => setSeeItems(true)}>See items</FakeLink>
      <Modal isOpen={seeItems} onRequestClose={closeModal}>
        <ModalContentWithCloseButton
          closeModal={closeModal}
          title="Quote breakdown"
        >
          <QuoteToolContainer>
            <ItemsModalContent>
              <QuoteShipments shipments={shipments} />
              <QuoteBreakdownItems items={items} currency={currency} />
            </ItemsModalContent>
          </QuoteToolContainer>
        </ModalContentWithCloseButton>
      </Modal>
    </>
  );
}

export default function QuoteHistory() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const { quoteHistory } = useQuoteHistory(boardId);

  const renderQuotes = () => {
    if (quoteHistory === null) {
      return <LoadingIcon text={"Loading history"} />;
    } else if (quoteHistory.quotes.length == 0) {
      return (
        <LineItemDescription>
          No quotes given or accepted yet
        </LineItemDescription>
      );
    } else {
      return quoteHistory.quotes.map((quote) => {
        return (
          <QuoteHistoryItem key={quote.datetime_created}>
            <div>
              <InputLabel>Type:</InputLabel>
              <div>{quote.quote_type}</div>
            </div>
            <div>
              <InputLabel>Total:</InputLabel>
              <div>{formatCurrency(quote.total, quote.currency)}</div>
            </div>
            <div>
              <InputLabel>Items:</InputLabel>
              <QuoteItems
                items={quote.items}
                shipments={quote.shipments}
                currency={quote.currency}
              />
            </div>
            <div>
              <InputLabel>Quoted at:</InputLabel>
              <div>
                {datetimeStringToHumanReadableDatetime(quote.datetime_created)}
              </div>
            </div>
          </QuoteHistoryItem>
        );
      });
    }
  };

  return (
    <QuoteHistoryContainer>
      <Title>History</Title>
      {renderQuotes()}
    </QuoteHistoryContainer>
  );
}
